import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { logoBase64 } from '../assets/logoBase64';
import { getAdmissionTypeLabel } from './admissionType';
import { getPublicReceiptUrl, getQrCodeDataUrl } from './receiptQr';

const processLogo = (base64Str) => new Promise((resolve) => {
  const img = new Image();
  img.src = base64Str.startsWith('data:') ? base64Str : 'data:image/webp;base64,' + base64Str;
  img.onload = () => {
    const cv = document.createElement('canvas');
    cv.width = img.naturalWidth;
    cv.height = img.naturalHeight;
    const ctx = cv.getContext('2d');
    ctx.drawImage(img, 0, 0);
    resolve({
      data: cv.toDataURL('image/png'),
      width: img.naturalWidth,
      height: img.naturalHeight
    });
  };
  img.onerror = () => resolve(null);
});

const makeSvgIcon = (svgStr) => new Promise((resolve) => {
  const img = new Image();
  img.src = 'data:image/svg+xml;base64,' + btoa(svgStr);
  img.onload = () => {
    const cv = document.createElement('canvas');
    cv.width = 512;
    cv.height = 512;
    const ctx = cv.getContext('2d');
    ctx.drawImage(img, 0, 0, 512, 512);
    resolve(cv.toDataURL('image/png'));
  };
  img.onerror = () => resolve(null);
});

export const generateAdmissionReceipt = async ({
  application,
  applicationId,
  course,
  program,
  paymentId,
  studentName = 'Student'
}) => {
  const doc = new jsPDF({ format: [210, 340] });
  const isCancelled = application?.status === 'cancelled';
  const issuedAt = application?.createdAt ? new Date(application.createdAt) : new Date();
  const dateStr = issuedAt.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Kolkata'
  });
  const timeStr = issuedAt.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Kolkata'
  });

  doc.setFillColor(252, 211, 77);
  doc.rect(0, 0, 5, 340, 'F');

  doc.setFillColor(0, 0, 0);
  doc.rect(5, 10, 200, 35, 'F');

  if (logoBase64) {
    const logo = await processLogo(logoBase64);
    if (logo) {
      const targetHeight = 14;
      const aspect = logo.width / logo.height;
      doc.addImage(logo.data, 'PNG', 15, 20, targetHeight * aspect, targetHeight);
    }
  }

  doc.setFontSize(20);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.text('OFFICIAL RECEIPT', 135, 30);

  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'bold');
  doc.text('ISSUED ON: ', 15, 55);
  doc.setFont('helvetica', 'normal');
  doc.text(`${dateStr} at ${timeStr}`, 40, 55);

  const resolvedApplicationId = applicationId || application?.applicationId || 'N/A';
  const resolvedPaymentId = paymentId || application?.paymentId || 'N/A';
  const digitalReceiptUrl = resolvedApplicationId !== 'N/A' ? getPublicReceiptUrl(resolvedApplicationId) : '';
  const qrCodeDataUrl = await getQrCodeDataUrl(digitalReceiptUrl);
  const fee = Number(application?.totalPaid || 0);
  const cancelledAt = application?.cancelledAt ? new Date(application.cancelledAt) : null;
  const cancelledDateTime = cancelledAt && !Number.isNaN(cancelledAt.getTime())
    ? `${cancelledAt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' })} at ${cancelledAt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}`
    : 'N/A';
  const cancelledBy = application?.cancelledBy?.name || application?.fullName || studentName || 'Student';
  const statusLabel = isCancelled ? 'PRE REGISTRATION CANCELLED' : 'PRE REGISTRATION CONFIRMED';
  const tableData = [
    ['INSTITUTION NAME', (application?.collegeName || course?.collegeName || 'SNS Institutions').toUpperCase()],
    ['APPLICATION ID', resolvedApplicationId],
    ['STUDENT NAME', studentName.toUpperCase()],
    ['PROGRAM', (program?.name || application?.programName || 'N/A').toUpperCase()],
    ['COURSE', (course?.name || application?.courseName || 'N/A').toUpperCase()],
    ['ADMISSION TYPE', getAdmissionTypeLabel(application?.admissionType).toUpperCase()],
    ['ACADEMIC YEAR', (application?.academicYear || 'N/A').toUpperCase()],
    ['AMOUNT PAID', `INR ${fee.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
    ['PAYMENT ID', resolvedPaymentId],
    ['DATE & TIME', `${dateStr} at ${timeStr}`],
    ['STATUS', statusLabel]
  ];

  if (isCancelled) {
    tableData.push(['CANCELLED', 'YES']);
    tableData.push(['CANCELLED ON', cancelledDateTime]);
    tableData.push(['CANCELLED BY', cancelledBy]);
  }

  if (qrCodeDataUrl) {
    doc.addImage(qrCodeDataUrl, 'PNG', 164, 50, 24, 24);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    // doc.text('SCAN DIGITAL RECEIPT', 176, 78, { align: 'center' });
  } else if (digitalReceiptUrl) {
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('DIGITAL RECEIPT:', 130, 58);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 102, 204);
    doc.text(digitalReceiptUrl, 130, 63, { maxWidth: 60 });
  }

  autoTable(doc, {
    startY: 80,
    head: [['DESCRIPTION', 'DETAILS']],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: [0, 0, 0],
      textColor: [255, 204, 0],
      fontSize: 10,
      fontStyle: 'bold',
      cellPadding: { top: 3, right: 5, bottom: 3, left: 5 }
    },
    bodyStyles: { fontSize: 9, cellPadding: { top: 3, right: 5, bottom: 3, left: 5 }, textColor: [0, 0, 0] },
    columnStyles: {
      0: { cellWidth: 55, fontStyle: 'bold', fillColor: [250, 250, 250] }
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 1) {
        const rowLabel = data.row.raw?.[0];
        if (rowLabel === 'STATUS') {
          data.cell.styles.textColor = isCancelled ? [220, 38, 38] : [0, 153, 51];
          data.cell.styles.fontStyle = 'bold';
        }
        if (isCancelled && (rowLabel === 'CANCELLED' || rowLabel === 'CANCELLED ON' || rowLabel === 'CANCELLED BY')) {
          data.cell.styles.textColor = [220, 38, 38];
          data.cell.styles.fontStyle = 'bold';
        }
      }
    }
  });

  const y = (doc.lastAutoTable && doc.lastAutoTable.finalY) || 160;
  const iconCheck = await makeSvgIcon('<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01" stroke="#16a34a" stroke-width="3"></polyline></svg>');
  const iconCal = await makeSvgIcon('<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="4" rx="2" ry="2" fill="#ffffff"></rect><rect width="18" height="6" x="3" y="4" rx="2" ry="2" fill="#444444" stroke="none"></rect><circle cx="8" cy="14" r="1.5" fill="#444444" stroke="none"/><circle cx="12" cy="14" r="1.5" fill="#444444" stroke="none"/><circle cx="16" cy="14" r="1.5" fill="#444444" stroke="none"/><circle cx="8" cy="18" r="1.5" fill="#444444" stroke="none"/><circle cx="12" cy="18" r="1.5" fill="#444444" stroke="none"/><circle cx="21" cy="21" r="5" fill="#f59e0b" stroke="none"/><text x="21" y="23.5" fill="#ffffff" font-size="8" font-family="sans-serif" font-weight="bold" stroke="none" text-anchor="middle">!</text></svg>');
  const iconMail = await makeSvgIcon('<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#444444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"></rect><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path></svg>');
  const iconPhone = await makeSvgIcon('<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#444444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="20" x="5" y="2" rx="2" ry="2"></rect><path d="M12 18h.01"></path></svg>');
  const iconGlobe = await makeSvgIcon('<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#444444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="2" x2="22" y1="12" y2="12"></line><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path></svg>');

  doc.setFillColor(isCancelled ? 254 : 245, isCancelled ? 242 : 249, isCancelled ? 242 : 246);
  doc.setDrawColor(isCancelled ? 254 : 230, isCancelled ? 202 : 240, isCancelled ? 202 : 230);
  doc.roundedRect(15, y + 6, 180, 15, 3, 3, 'FD');
  doc.addImage(iconCheck, 'PNG', 22, y + 9.5, 8, 8);
  doc.setFontSize(7);
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'bold');
  doc.text(isCancelled ? 'PRE REGISTRATION CANCELLED' : 'VERIFIED ENROLLMENT', 36, y + 12);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5.8);
  doc.text(
    isCancelled
      ? `This receipt confirms the booking was cancelled${cancelledDateTime !== 'N/A' ? ` on ${cancelledDateTime}.` : '.'}`
      : 'This receipt confirms your seat reservation and initial fee payment in our system.',
    36,
    y + 17,
    { maxWidth: 150 }
  );

  doc.setFillColor(255, 248, 240);
  doc.setDrawColor(255, 235, 204);
  doc.roundedRect(15, y + 25, 180, 15, 3, 3, 'FD');
  doc.addImage(iconCal, 'PNG', 22, y + 28.5, 8, 8);
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'bold');
  doc.text('NOTE:', 36, y + 33);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5.8);
  if (isCancelled) {
    doc.text('Cancelled pre-registration. The paid amount remains non-refundable as per policy.', 52, y + 33, { maxWidth: 130 });
  } else {
    doc.text('This is a temporary seat confirmation only. Students are required to visit the institution campus', 42, y + 51);
    doc.text('directly to verify and confirm their course admission.', 42, y + 57);
  }

  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'bold');
  doc.text('Need Help?', 17, y + 54);

  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.addImage(iconMail, 'PNG', 17, y + 58, 6, 5);
  doc.setTextColor(30, 30, 30);
  doc.text('support@seatifyai.com', 26, y + 62);
  doc.setTextColor(180, 180, 180);
  doc.text('|', 78, y + 62);
  doc.addImage(iconPhone, 'PNG', 82, y + 57.5, 5, 6);
  doc.setTextColor(30, 30, 30);
  doc.text('+91 96583 75375', 90, y + 62);
  doc.setTextColor(180, 180, 180);
  doc.text('|', 123, y + 62);
  doc.addImage(iconGlobe, 'PNG', 127, y + 57.5, 6, 6);
  doc.setTextColor(0, 102, 204);
  doc.text('www.seatifyai.com', 136, y + 62);

  doc.setDrawColor(255, 180, 0);
  doc.setLineWidth(0.8);
  doc.line(82, y + 73, 99, y + 73);
  doc.line(111, y + 73, 128, y + 73);
  doc.setFillColor(255, 180, 0);
  doc.setDrawColor(255, 180, 0);
  doc.circle(105, y + 73, 1.5, 'FD');
  doc.setTextColor(0, 0, 120);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  if (isCancelled) {
    doc.text('Thank you for choosing Seatify.', 105, y + 80, { align: 'center', maxWidth: 150 });
    doc.text('Wishing you success and new opportunities in your journey.', 105, y + 86, { align: 'center', maxWidth: 150 });
  } else {
    doc.text('Take the first step today with Seatify', 105, y + 80, { align: 'center', maxWidth: 150 });
    doc.text('towards a successful future.', 105, y + 86, { align: 'center', maxWidth: 150 });
  }

  doc.setDrawColor(210, 210, 210);
  doc.setLineWidth(0.3);
  doc.line(15, y + 94, 195, y + 94);
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.setFont('helvetica', 'normal');
  doc.text('This is a digital receipt issued by SeatifyAI Admission System. www.seatifyai.com | Page 1 of 1', 105, y + 101, { align: 'center' });

  doc.save(`Seatify_Receipt_${resolvedApplicationId}.pdf`);
};
